/**
 * KRA Photo Journal – App Root
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { AppHeader } from "./components/AppHeader.js";
import { FilterBar } from "./components/FilterBar.js";
import { PhotoGrid } from "./components/PhotoGrid.js";
import { LoginPage } from "./components/LoginPage.js";
import { KioskGate } from "./components/KioskGate.js";
import { PublicGallery } from "./components/PublicGallery.js";
import { AdminPanel } from "./components/AdminPanel.js";
import { BulkImportPanel } from "./components/BulkImportPanel.js";
import { BulkSortPanel } from "./components/BulkSortPanel.js";
import { AutoTagPanel } from "./components/AutoTagPanel.js";
import { CollectionPanel } from "./components/CollectionPanel.js";
import { usePhotos, type SortField, type SortDir } from "./hooks/usePhotos.js";
import { usePhotographers } from "./hooks/usePhotographers.js";
import { useAuth } from "./hooks/useAuth.js";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { matchesTagFilters, type TagFilters } from "./lib/tagHelpers.js";
import {
  fetchTagOptions,
  fetchCollection,
  fetchDriveFolders,
  type TagOptions,
  type DriveFolderCount,
} from "./lib/api.js";
import { loadInitialFilters, syncFilters } from "./hooks/useFilterPersistence.js";
import { Toaster } from "./components/Toaster.js";

const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";

const PRIORITY_PHOTOGRAPHERS = ["Duy Phú Quý", "Đức Phú", "Nhâm", "Long Cao", "Tôn", "Tuấn", "Tân", "Sơn", "Linh", "San San", "Admin"];
const TEMP_ACCESS_KEY = "kra_temp_access";
const KIOSK_KEY = "kra_kiosk";

export type QuickFilter = "all" | "liked" | "downloaded" | "not-downloaded";

export default function App() {
  // ── Public share viewer — short-circuits the entire staff flow ──────────
  // Read once at module evaluation: if the URL has ?share=<token>, render
  // the customer-facing read-only gallery and skip login / kiosk entirely.
  const [shareToken] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const t = new URL(window.location.href).searchParams.get("share");
    return t && /^[A-Za-z0-9]{16}$/.test(t) ? t : null;
  });
  if (shareToken) {
    return <PublicGallery token={shareToken} />;
  }

  const { name: currentUser, role, isLoading: authLoading, login, logout } = useAuth();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showBulkSort, setShowBulkSort] = useState(false);
  const [showAutoTag, setShowAutoTag] = useState(false);
  const [showCollections, setShowCollections] = useState(false);
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const [activeCollectionPhotoIds, setActiveCollectionPhotoIds] = useState<Set<string> | null>(null);
  const [initialRecordId, setInitialRecordId] = useState<string | null>(null);

  // ── Kiosk mode ─────────────────────────────────────────────────────────────
  // isKiosk: true if ?kiosk=1 was ever visited on this device
  const [isKiosk] = useState(() => localStorage.getItem(KIOSK_KEY) === "1");
  // kioskReady: true after location check passes in KioskGate
  const [kioskReady, setKioskReady] = useState(false);

  // ── URL params on mount ────────────────────────────────────────────────────
  const urlParsed = useRef(false);
  useEffect(() => {
    if (urlParsed.current) return;
    urlParsed.current = true;

    const url = new URL(window.location.href);

    // ?kiosk=1 → activate kiosk mode permanently on this device
    if (url.searchParams.has("kiosk")) {
      localStorage.setItem(KIOSK_KEY, "1");
      url.searchParams.delete("kiosk");
      window.history.replaceState({}, "", url.toString());
      window.location.reload(); // reload so isKiosk state is picked up
      return;
    }

    // ?access=xxx → temp location bypass for photographers
    const accessToken = url.searchParams.get("access");
    if (accessToken) {
      sessionStorage.setItem(TEMP_ACCESS_KEY, accessToken);
      url.searchParams.delete("access");
      window.history.replaceState({}, "", url.toString());
    }

    // ?record=recXXX → deep link to a specific photo record (from Caption Workstation)
    // Clear filters so the linked record is reachable regardless of current view.
    const deepLinkRecord = url.searchParams.get("record");
    if (deepLinkRecord) {
      setInitialRecordId(deepLinkRecord);
      setSelectedPhotographer(null);
      setSearch("");
      setQuickFilter("all");
      setTagFilters({});
      setMonthFilter(null);
      url.searchParams.delete("record");
      window.history.replaceState({}, "", url.toString());
    }

    // ?t=xxx → admin bypass
    const adminToken = url.searchParams.get("t");
    if (adminToken) {
      fetch(`${API_BASE}/auth/admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: adminToken }),
      })
        .then(async (res) => {
          if (res.ok) {
            const data = (await res.json()) as { token: string; name: string };
            login(data.token, data.name);
          }
        })
        .catch(() => {/* ignore */})
        .finally(() => {
          url.searchParams.delete("t");
          window.history.replaceState({}, "", url.toString());
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effective user ─────────────────────────────────────────────────────────
  // Regular login overrides kiosk (e.g. photographer picks up the iPad).
  // "Kiosk" is a special read-only user when kiosk mode is active.
  const effectiveUser = currentUser ?? (isKiosk && kioskReady ? "Kiosk" : null);
  const isReadOnly = effectiveUser === "Kiosk";
  // Admin = invite-code role "admin" OR legacy PIN login where name was "Admin".
  const isAdmin = role === "admin" || effectiveUser === "Admin";

  // ── Main app state ────────────────────────────────────────────────────────
  // Filters are restored from URL params (priority) → localStorage (fallback)
  // → defaults. See hooks/useFilterPersistence.ts.
  const [persistedInitial] = useState(loadInitialFilters);
  const [selectedPhotographer, setSelectedPhotographer] = useState<string | null>(persistedInitial.selectedPhotographer);
  const [sortField, setSortField] = useState<SortField>(persistedInitial.sortField);
  const [sortDir, setSortDir] = useState<SortDir>(persistedInitial.sortDir);
  const [search, setSearch] = useState(persistedInitial.search);
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(persistedInitial.quickFilter);
  const [tagFilters, setTagFilters] = useState<TagFilters>(persistedInitial.tagFilters);
  const [tagOptions, setTagOptions] = useState<TagOptions | null>(null);
  const [monthFilter, setMonthFilter] = useState<string | null>(persistedInitial.monthFilter); // "YYYY-MM"
  // Drive-folder filter (adminCategoryFolder). Applied server-side by usePhotos.
  // Not persisted — it's data-driven and only meaningful once records have
  // been categorised.
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [driveFolders, setDriveFolders] = useState<DriveFolderCount[]>([]);

  // Sync filter state to URL + localStorage on every change
  useEffect(() => {
    syncFilters({ selectedPhotographer, sortField, sortDir, search, quickFilter, tagFilters, monthFilter });
  }, [selectedPhotographer, sortField, sortDir, search, quickFilter, tagFilters, monthFilter]);

  // Fetch tag field options (defined in Airtable schema) once on mount
  useEffect(() => {
    fetchTagOptions()
      .then(setTagOptions)
      .catch((err) => console.warn("[App] fetchTagOptions failed:", err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drive category folders for the folder pills (KV-cached server-side).
  // `refresh` rescans — called after the admin backfill fills new folders.
  const loadDriveFolders = useCallback((refresh = false) => {
    fetchDriveFolders(refresh)
      .then(setDriveFolders)
      .catch((err) => console.warn("[App] fetchDriveFolders failed:", err));
  }, []);

  useEffect(() => {
    loadDriveFolders();
  }, [loadDriveFolders]);

  // Convert collection photo IDs to an array for server-side filtering
  // Stable reference — without useMemo, Array.from() returns a new array
  // every render, causing usePhotos' effect to refetch in an infinite loop.
  const collectionRecordIds = useMemo(
    () => (activeCollectionPhotoIds ? Array.from(activeCollectionPhotoIds) : undefined),
    [activeCollectionPhotoIds],
  );

  // Quick filters run server-side. The feed pages 48 records at a time and the
  // first liked set can sit hundreds of records deep, so filtering the loaded
  // page client-side used to show "No photos found" with no way to page further.
  // A photographer sees only their own likes; admin/kiosk see every like.
  const likedByFilter =
    quickFilter === "liked" && effectiveUser && !isAdmin && !isReadOnly ? effectiveUser : undefined;

  const { photos, isLoading, isLoadingMore, hasMore, error, loadMore, updatePhoto } = usePhotos({
    photographer: selectedPhotographer ?? undefined,
    sortField,
    sortDir,
    search: search.trim() || undefined,
    recordIds: collectionRecordIds,
    folder: categoryFilter ?? undefined,
    quick: quickFilter === "all" ? undefined : quickFilter,
    likedBy: likedByFilter,
  });

  const { photographers: allPhotographers } = usePhotographers();

  const photographers = [
    ...PRIORITY_PHOTOGRAPHERS.filter((name) => allPhotographers.includes(name)),
    ...allPhotographers
      .filter((name) => !PRIORITY_PHOTOGRAPHERS.includes(name))
      .sort((a, b) => a.localeCompare(b, "vi")),
  ];

  // Load collection photo IDs when a collection is selected
  useEffect(() => {
    if (!activeCollectionId) {
      setActiveCollectionPhotoIds(null);
      return;
    }
    fetchCollection(activeCollectionId)
      .then((col) => setActiveCollectionPhotoIds(new Set(col.photoIds)))
      .catch(() => {
        setActiveCollectionId(null);
        setActiveCollectionPhotoIds(null);
      });
  }, [activeCollectionId]);

  // Quick filters are already applied server-side by usePhotos — only the
  // month and tag filters still narrow the loaded slice here.
  const filteredPhotos = useMemo((): PhotoRecord[] => {
    let result = photos;

    // Month filter (by shootDate)
    if (monthFilter) {
      result = result.filter((p) => p.shootDate?.startsWith(monthFilter));
    }

    // Tag filters (AND between groups, OR within group)
    result = result.filter((p) => matchesTagFilters(p, tagFilters));

    return result;
  }, [photos, tagFilters, monthFilter]);

  const handleSelectPhotographer = useCallback((name: string | null) => setSelectedPhotographer(name), []);
  const handleSortFieldChange   = useCallback((f: SortField) => setSortField(f), []);
  const handleSortDirChange     = useCallback((d: SortDir) => setSortDir(d), []);
  const handleSearch = useCallback((q: string) => {
    setSearch(q);
    if (q.trim()) setSelectedPhotographer(null);
  }, []);
  const handleQuickFilter = useCallback((f: QuickFilter) => setQuickFilter(f), []);
  const handleTagFilters = useCallback((f: TagFilters) => setTagFilters(f), []);
  const handleMonthFilter = useCallback((m: string | null) => setMonthFilter(m), []);
  const handleCategoryFilter = useCallback((c: string | null) => setCategoryFilter(c), []);

  // Click a tag chip on a card → toggle that tag in the active filters for its group
  const handleTagClick = useCallback((groupKey: string, tagValue: string) => {
    setTagFilters((prev) => {
      const current = prev[groupKey as keyof TagFilters] ?? [];
      const next = current.includes(tagValue)
        ? current.filter((v) => v !== tagValue)
        : [...current, tagValue];
      return { ...prev, [groupKey]: next };
    });
  }, []);

  // ── Auth / kiosk guard ────────────────────────────────────────────────────

  // Kiosk mode: if no regular user, show KioskGate for location check
  if (isKiosk && !currentUser && !kioskReady) {
    return <KioskGate onEnter={() => setKioskReady(true)} />;
  }

  // Auth loading (verifying stored JWT)
  if (authLoading && !isKiosk) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <div className="text-white/30 text-sm animate-pulse">Đang tải…</div>
      </div>
    );
  }

  // Not logged in, not kiosk
  if (!effectiveUser) {
    return <LoginPage onLogin={login} />;
  }

  // ── Main app ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface-0">
      <AppHeader
        currentUser={effectiveUser}
        isReadOnly={isReadOnly}
        onLogout={isReadOnly ? undefined : logout}
        onBulkImport={isAdmin ? () => setShowBulkImport(true) : undefined}
        onBulkSort={isAdmin ? () => setShowBulkSort(true) : undefined}
        onAutoTag={isAdmin ? () => setShowAutoTag(true) : undefined}
        onAdminPanel={isAdmin ? () => setShowAdminPanel(true) : undefined}
        onCollections={isAdmin ? () => setShowCollections(true) : undefined}
        activeCollectionId={activeCollectionId}
        onClearCollection={() => setActiveCollectionId(null)}
      />

      <FilterBar
        photographers={photographers}
        selectedPhotographer={selectedPhotographer}
        onSelectPhotographer={handleSelectPhotographer}
        sortField={sortField}
        sortDir={sortDir}
        onSortFieldChange={handleSortFieldChange}
        onSortDirChange={handleSortDirChange}
        totalCount={filteredPhotos.length}
        search={search}
        onSearch={handleSearch}
        quickFilter={quickFilter}
        onQuickFilter={handleQuickFilter}
        tagFilters={tagFilters}
        onTagFilters={handleTagFilters}
        monthFilter={monthFilter}
        onMonthFilter={handleMonthFilter}
        driveFolders={driveFolders}
        categoryFilter={categoryFilter}
        onCategoryFilter={handleCategoryFilter}
        allPhotos={photos}
        tagOptions={tagOptions ?? undefined}
        isReadOnly={isReadOnly}
        isLoading={isLoading}
      />

      <main>
        <PhotoGrid
          photos={filteredPhotos}
          allPhotos={photos}
          tagOptions={tagOptions ?? undefined}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          hasMore={hasMore}
          onLoadMore={loadMore}
          error={error}
          onUpdatePhoto={updatePhoto}
          currentUser={effectiveUser}
          isAdmin={isAdmin}
          isReadOnly={isReadOnly}
          onTagClick={handleTagClick}
          initialRecordId={initialRecordId}
          onInitialRecordConsumed={() => setInitialRecordId(null)}
        />
      </main>

      {showAdminPanel && (
        <AdminPanel
          onClose={() => setShowAdminPanel(false)}
          isAdmin={isAdmin}
          onFoldersChanged={() => loadDriveFolders(true)}
        />
      )}

      {showBulkImport && (
        <BulkImportPanel onClose={() => setShowBulkImport(false)} />
      )}

      {showBulkSort && (
        <BulkSortPanel onClose={() => setShowBulkSort(false)} />
      )}

      {showAutoTag && (
        <AutoTagPanel onClose={() => setShowAutoTag(false)} />
      )}

      {showCollections && (
        <CollectionPanel
          onClose={() => setShowCollections(false)}
          onSelectCollection={setActiveCollectionId}
        />
      )}

      <Toaster />
    </div>
  );
}
